/**
 * CollapsibleSection — Apple-clean, foldable dashboard section.
 *
 * Drop-in wrapper that renders a rounded card with a header (icon + title +
 * eyebrow + chevron) and a body that smoothly folds/unfolds on click. State
 * is persisted to localStorage per-section so admins keep their layout
 * preference across reloads.
 *
 * Visual language:
 *   - rounded-2xl, hairline gray-200 border, white surface
 *   - 22px / extrabold title, 10px / tracking-[0.22em] eyebrow above
 *   - chevron rotates 90° on toggle, 200ms ease-out
 *   - subtle hover state on the entire header (cursor-pointer)
 *   - body content padded p-5 / sm:p-6 to match Apple settings panes
 *
 * Usage:
 *   <CollapsibleSection
 *     id="overview-ops"
 *     title="Operations"
 *     eyebrow="Cron · Image purge · Test cleanup"
 *     icon={Activity}
 *   >
 *     {children}
 *   </CollapsibleSection>
 */
import React, { useEffect, useState } from 'react';
import { ChevronRight, Star } from 'lucide-react';

const STORAGE_KEY = 'zhelta.admin.section.';
const PIN_KEY = 'zhelta.admin.section.pin.';

export default function CollapsibleSection({
  id,
  title,
  eyebrow,
  icon: Icon,
  defaultOpen = true,
  children,
  rightAction = null,
  testId,
}) {
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return defaultOpen;
    const v = window.localStorage.getItem(STORAGE_KEY + id);
    if (v === '1') return true;
    if (v === '0') return false;
    return defaultOpen;
  });

  // Pinned sections rise to the top of the parent flex container via CSS
  // `order: -1`. Pin state is per-admin (localStorage) so each role can
  // curate their own dashboard without affecting others.
  const [pinned, setPinned] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(PIN_KEY + id) === '1';
  });

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY + id, open ? '1' : '0'); }
    catch { /* ignore quota */ }
  }, [id, open]);

  useEffect(() => {
    try { window.localStorage.setItem(PIN_KEY + id, pinned ? '1' : '0'); }
    catch { /* ignore quota */ }
  }, [id, pinned]);

  return (
    <section
      data-testid={testId || `section-${id}`}
      data-pinned={pinned ? '1' : '0'}
      style={{ order: pinned ? -1 : 0 }}
      className={`rounded-2xl border bg-white overflow-hidden transition-shadow duration-200 hover:shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${pinned ? 'border-[#c9a227] ring-1 ring-[#c9a227]/20' : 'border-gray-200'}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`section-toggle-${id}`}
        className="w-full px-5 sm:px-6 py-4 flex items-center gap-3 group hover:bg-gray-50/60 transition-colors"
      >
        {Icon && (
          <div className="shrink-0 w-9 h-9 rounded-full bg-[#fffbe9] grid place-items-center text-[#a8871a]">
            <Icon className="w-4 h-4" />
          </div>
        )}
        <div className="min-w-0 text-left flex-1">
          {eyebrow && (
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">{eyebrow}</div>
          )}
          <h3 className="text-[15px] sm:text-[16px] font-extrabold text-gray-900 truncate">{title}</h3>
        </div>
        {rightAction && (
          <div className="shrink-0" onClick={(e) => e.stopPropagation()} role="presentation">
            {rightAction}
          </div>
        )}
        {/* Pin / unpin — keeps clicks isolated from the toggle button */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setPinned((p) => !p); }}
          aria-pressed={pinned}
          data-testid={`section-pin-${id}`}
          title={pinned ? 'Unpin from top' : 'Pin to top'}
          className={`shrink-0 p-1.5 rounded-full transition-colors ${pinned ? 'text-[#c9a227] hover:bg-amber-50' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'}`}
        >
          <Star className={`w-3.5 h-3.5 ${pinned ? 'fill-[#c9a227]' : ''}`} />
        </button>
        <ChevronRight
          className={`shrink-0 w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div
          className="px-5 sm:px-6 pb-5 sm:pb-6 pt-1 flex flex-col gap-4 border-t border-gray-100"
          data-testid={`section-body-${id}`}
        >
          {children}
        </div>
      )}
    </section>
  );
}
